import {
  DAY_SECS,
  HR_SECS,
  MONTH_SECS,
  WEEK_SECS,
  YEAR_SECS,
} from './constants';
import { DateRange, DateRangeLabel, Order, TimeInterval } from './types';

const sortObjectArrayByDate = <T>(
  objArray: T[],
  order: Order,
  getDateFn: (obj: T) => Date
) =>
  objArray.sort((a: T, b: T) => {
    switch (order) {
      case 'desc':
        return (
          new Date(getDateFn(b)).getTime() - new Date(getDateFn(a)).getTime()
        );
      case 'asc':
        return (
          new Date(getDateFn(a)).getTime() - new Date(getDateFn(b)).getTime()
        );
      default:
        const exhaustiveCheck: never = order;
        return exhaustiveCheck;
    }
  });

function getWeekRange(date?: Date) {
  const dateCopy = date ? new Date(date) : new Date();
  const currentDayOfTheMonth = dateCopy.getDate();
  const currentDayOfTheWeek = dateCopy.getDay();
  const daysArray = Array(7).fill(null);
  const dayOfTheWeek = new Date(
    dateCopy.setDate(currentDayOfTheMonth - currentDayOfTheWeek)
  );
  const weekRange = daysArray.map((day, index) => {
    const increment = index === 0 ? 0 : 1;
    const newDate = new Date(
      dayOfTheWeek.setDate(dayOfTheWeek.getDate() + increment)
    );
    if (index === 0) newDate.setHours(0, 0, 0, 0);
    if (index === 6) {
      newDate.setHours(23, 59, 59, 999);
    }
    return newDate;
  });
  return weekRange;
}

function getNewDateFromOffset(currentDate: Date, offsetByDays: number) {
  return new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate() + offsetByDays
  );
}

function getSecondsInTimeInterval(timeInterval: TimeInterval): number {
  switch (timeInterval) {
    case 'hour':
      return HR_SECS;
    case 'day':
      return DAY_SECS;
    case 'week':
      return WEEK_SECS;
    case 'month':
      return MONTH_SECS;
    case 'year':
      return YEAR_SECS;
  }
}

const getEpochTime = (date: Date) => Math.round(date.getTime() / 1000);

const getDateFromEpochTime = (epochTime: number) =>
  new Date(new Date(0).setUTCSeconds(epochTime));

const getDateInTheFuture = (
  startingDate: Date,
  dayCountInTheFuture: number
) => {
  const date = new Date(startingDate.getTime());
  date.setDate(date.getDate() + dayCountInTheFuture);
  return date;
};

const getDateInThePast = (startingDate: Date, dayCountInThePast: number) => {
  const date = new Date(startingDate.getTime());
  date.setDate(date.getDate() - dayCountInThePast);
  return date;
};

function getDateRangeFromLabel(label: DateRangeLabel): DateRange {
  const today = new Date();
  switch (label) {
    case 'Yesterday':
      return {
        startDateRange: getNewDateFromOffset(today, -1),
        endDateRange: today,
      };
    case 'Today':
      return {
        startDateRange: today,
        endDateRange: today,
      };
    case 'Last 7 days':
      return {
        startDateRange: getNewDateFromOffset(today, -7),
        endDateRange: today,
      };
    case 'Last 30 days':
      return {
        startDateRange: getNewDateFromOffset(today, -30),
        endDateRange: today,
      };
    case 'Last 90 days':
      return {
        startDateRange: getNewDateFromOffset(today, -90),
        endDateRange: today,
      };
    case 'All time':
      return { startDateRange: null, endDateRange: null };
  }
}

export {
  sortObjectArrayByDate,
  getWeekRange,
  getNewDateFromOffset,
  getSecondsInTimeInterval,
  getEpochTime,
  getDateFromEpochTime,
  getDateInTheFuture,
  getDateInThePast,
  getDateRangeFromLabel,
};
